import {
	catchError, finalize, Observable, of, take, tap,
} from 'rxjs';

import {
	AuthService,
	Container, LearningExperienceType, Service, history,
} from '../../../../symphony';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import { SlActions } from '../ActionsService/sl-actions';
import {
	ActionType,
	IError,
	IUpdatedCodeTemplate,
	RequestType,
	TrackActionName,
} from '../../global-interface';
import { SlCommonService } from './sl-common.service';
import { ProgrammingLanguageToCodeLanguages } from '../../../../shared/public/constants/constants';
import { SlErrorHandlerService } from '../../../../api/public/error-handling';
import {
	IMaterialInfo, IMaterialStatus, ISolveMaterialDTO, ISolveMaterialPayload, ITIYMaterialData,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { IUserCode } from '../../../../api/public/code-api';
import { CodeLanguages, ErrorCodes } from '../../../../api/private/global.interface';
import { SlLEContext } from '../../../../learnEngine/private/global-constants';
import { SlDataService as SlLEDataService } from '../../../../learnEngine/private/services/sl-data.service';
import { getLETypeFromRoute } from '../../../../learnEngine/private/utils/helpers';
import { lePlaygroundContext } from '../../global-constants';
import { SlTrackingService } from './sl-tracking.service';
import { checkIsWeb } from '../../../../shared/public/SlCodeEditorComponents/utils/helperFunctions';

@Service()
export class SlMaterialService {
	private api = Container.take(lePlaygroundContext.value, SlApiService);

	private dataService = Container.take(lePlaygroundContext.value, SlDataService);

	private errorHandler = Container.take(lePlaygroundContext.value, 'errorHandler');

	private actions$ = Container.take(lePlaygroundContext.value, SlActions).actions$;

	private leData = Container.take(SlLEContext, SlLEDataService);

	private handleError = Container.take(
		lePlaygroundContext.value,
		SlErrorHandlerService,
	).handleError;

	private trackAction$ = Container.take(
		lePlaygroundContext.value,
		SlTrackingService,
	).trackAction$;

	private commonService = Container.take(
		lePlaygroundContext.value,
		SlCommonService,
	);

	private authService = Container.take('global', AuthService);

	public setCodeData = (
		code: IUserCode | IUpdatedCodeTemplate,
	): Observable<null> => {
		let localCodeData = { ...code };
		const {
			isWeb$,
			sourceCode$,
			cssCode$,
			jsCode$,
			codeName$,
			codeData$,
			showConfirmLeaveAlert$,
		} = this.dataService;

		showConfirmLeaveAlert$.next(false);
		if (
			code?.userId
			&& this.authService.getUser()?.id !== localCodeData?.userId
		) {
			this.trackAction$.next({
				action: 'view',
				element: 'codeplayground_other_usercodes',
				entityId: localCodeData?.id,
				userId: this.authService.getUser()?.id.toString(),
			});
		}
		if (!code.language || code.language === CodeLanguages.RAW) {
			const language: CodeLanguages = Container.take(
				lePlaygroundContext.value,
				'language',
			);
			localCodeData = {
				...code,
				language,
			};
		}
		const localIsWeb = checkIsWeb(localCodeData.language);
		if (localIsWeb) {
			isWeb$.next(localIsWeb);
		}
		if (localCodeData.name) {
			codeName$.next(localCodeData.name);
		}
		sourceCode$.next(localCodeData.sourceCode);
		jsCode$.next(localCodeData.jsCode);
		cssCode$.next(localCodeData.cssCode);
		codeData$.next(localCodeData);

		return of(null);
	};

	public handleGetMaterialResponse = (material: IMaterialInfo): void => {
		let code: IUserCode;

		(material.data as ITIYMaterialData).codes.forEach((item) => {
			switch (ProgrammingLanguageToCodeLanguages[item.languageId]) {
				case CodeLanguages.JS: {
					code = {
						...code,
						jsCode: item.code,
						language: CodeLanguages.WEB,
					};
					break;
				}
				case CodeLanguages.CSS: {
					code = {
						...code,
						cssCode: item.code,
						language: CodeLanguages.WEB,
					};
					break;
				}
				case CodeLanguages.HTML: {
					code = {
						...code,
						sourceCode: item.code,
						language: CodeLanguages.WEB,
					};
					break;
				}
				default: {
					code = {
						...code,
						language:
							ProgrammingLanguageToCodeLanguages[item.languageId],
						sourceCode: item.code,
					};
				}
			}
		});
		this.dataService.materialData$.next(material);
		if (!this.dataService.codeData$.value) {
			this.setCodeData(code);
		}

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.OnMaterialMount,
					materialInfo: this.dataService.materialData$.value,
				},
			},
		});
	};

	private handleMaterialSolve = (data: ISolveMaterialDTO): void => {
		if (
			data.statusChanges
			&& !this.dataService.materialData$.value.status.isCompleted
		) {
			const solveMaterialCallback: (data: IMaterialStatus[]) => void = Container.take(lePlaygroundContext.value, 'solveMaterialCallback');
			solveMaterialCallback(data.statusChanges);
		}

		const { materialData$ } = this.dataService;
		materialData$.next({
			...materialData$.value,
			status: {
				...materialData$.value.status,
				isCompleted: true,
			},
		});

		this.dataService.output$.next({
			output: data.solutions[0].codes[0].codeOutput.output,
			outputType: data.solutions[0].codes[0].codeOutput.outputType,
		});

		this.actions$.next({
			type: ActionType.Track,
			requestType: RequestType.Parallel,
			data: {
				trackData: {
					actionName: TrackActionName.MaterialSolve,
				},
			},
		});
	};

	private navigateToErrorPage = (): void => {
		history.push('/error');
	};

	private solveMaterialErrorHandler = (err: IError[]) => {
		const routeData = this.dataService.routeData$?.value;
		if (!Array.isArray(err)) {
			this.navigateToErrorPage();
			return;
		}
		this.handleError(err, {
			[ErrorCodes.BadRequest]: () => {
				this.navigateToErrorPage();
			},
			[ErrorCodes.NotFound]: () => {
				this.navigateToErrorPage();
			},
			[ErrorCodes.Forbidden]: () => {
				this.navigateToErrorPage();
			},
			[ErrorCodes.NotEnoughHearts]: () => {
				this.commonService.navigate(routeData?.returnUrl || '/learn');
			},
			[ErrorCodes.ExpiredSession]: () => {
				if (this.errorHandler[ErrorCodes.ExpiredSession]) {
					this.errorHandler[ErrorCodes.ExpiredSession]();
				} else {
					this.commonService.navigate(
						routeData?.returnUrl || '/learn',
					);
				}
			},
		});
	};

	public solveMaterial = (
		payload: ISolveMaterialPayload,
	): void => {
		this.dataService.loading$.next(true);

		if (getLETypeFromRoute(this.leData.routeData$.value) === LearningExperienceType.LearningPath) {
			payload.bundleId = this.leData.learningPath$.value?.bundleId;
		}

		this.api.solveMaterial(payload).pipe(
			tap(this.handleMaterialSolve),
			catchError((err) => {
				this.solveMaterialErrorHandler(err);
				return of(null);
			}),
			finalize(() => {
				this.dataService.loading$.next(false);
			}),
			take(1),
		).subscribe();
	};

	private isInNotFoundMaterialPage = (): boolean => location.pathname.includes('not-found-material');

	private getMaterialErrorHandler = (err: IError[]) => {
		const returnUrl = this.dataService.routeData$.value?.returnUrl || null;
		if (!Array.isArray(err)) {
			if (!this.isInNotFoundMaterialPage()) {
				this.commonService.navigate(
					`${window.location.pathname}/not-found-material${returnUrl ? `?returnurl=${returnUrl}` : ''
					}`,
				);
			}
			return;
		}
		this.handleError(err, {
			[ErrorCodes.BadRequest]: () => {
				if (!this.isInNotFoundMaterialPage()) {
					this.commonService.navigate(
						`${window.location.pathname}/not-found-material${returnUrl ? `?returnurl=${returnUrl}` : ''
						}`,
					);
				}
			},
			[ErrorCodes.NotFound]: () => {
				if (!this.isInNotFoundMaterialPage()) {
					this.commonService.navigate(
						`${window.location.pathname}/not-found-material${returnUrl ? `?returnurl=${returnUrl}` : ''
						}`,
					);
				}
			},
			[ErrorCodes.Forbidden]: () => {
				this.commonService.navigate(returnUrl || '/learn');
			},
			[ErrorCodes.NotEnoughHearts]: () => {
				this.commonService.navigate(returnUrl || '/learn');
			},
			[ErrorCodes.ExpiredSession]: () => {
				if (this.errorHandler?.[ErrorCodes.ExpiredSession]) {
					this.errorHandler[ErrorCodes.ExpiredSession]();
				} else {
					this.commonService.navigate(returnUrl || '/learn');
				}
			},
		});
	};

	public getMaterial = (materialId: number): Observable<IMaterialInfo> => {
		this.dataService.loading$.next(true);

		return this.api.getMaterial(materialId).pipe(
			tap(this.handleGetMaterialResponse),
			catchError((err) => {
				this.getMaterialErrorHandler(err);
				return of(null);
			}),
			finalize(() => {
				this.dataService.loading$.next(false);
			}),
		);
	};
}
